import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { MapPin, Search, Home, Fish, ArrowLeft, Compass } from "lucide-react";
import { useState } from "react";

export default function NotFound() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/destinations?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const popularDestinations = [
    { name: "Madison River", state: "Montana", slug: "madison-river-montana" },
    { name: "Green River", state: "Utah", slug: "green-river-utah" },
    { name: "South Platte River", state: "Colorado", slug: "south-platte-river-colorado" },
  ];

  return (
    <>
      <Helmet>
        <title>Page Not Found | DIY Fly Fishing Guide</title>
        <meta name="description" content="The page you're looking for doesn't exist. Explore our fly fishing destinations or search for your next adventure." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50 flex flex-col">
        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-2xl w-full text-center">
            {/* 404 Illustration */}
            <div className="relative mb-8">
              <div className="text-[150px] sm:text-[200px] font-bold text-blue-100 leading-none select-none">
                404
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Fish className="w-20 h-20 sm:w-28 sm:h-28 text-blue-500 animate-bounce" />
              </div>
            </div>

            {/* Message */}
            <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl text-slate-800 mb-4">
              This Fish Got Away
            </h1>
            <p className="text-slate-600 text-lg mb-8 max-w-md mx-auto">
              The page you&apos;re looking for seems to have swum downstream. Let&apos;s get you back to the good waters.
            </p>

            {/* Search Box */}
            <form onSubmit={handleSearch} className="mb-8">
              <div className="flex max-w-md mx-auto">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search destinations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-l-lg border border-r-0 border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="404-search-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="rounded-l-none px-6"
                  data-testid="404-search-btn"
                >
                  Search
                </Button>
              </div>
            </form>

            {/* Quick Links */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="gap-2"
                data-testid="404-go-back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </Button>
              <Button
                variant="outline"
                asChild
                className="gap-2"
              >
                <Link to="/" data-testid="404-home-link">
                  <Home className="w-4 h-4" />
                  Home
                </Link>
              </Button>
              <Button
                variant="outline"
                asChild
                className="gap-2"
              >
                <Link to="/destinations" data-testid="404-destinations-link">
                  <Compass className="w-4 h-4" />
                  Explore Map
                </Link>
              </Button>
            </div>

            {/* Popular Destinations */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <h2 className="font-medium text-slate-800 mb-4 flex items-center justify-center gap-2">
                <MapPin className="w-5 h-5 text-blue-500" />
                Popular Destinations
              </h2>
              <div className="grid sm:grid-cols-3 gap-3">
                {popularDestinations.map((dest) => (
                  <Link
                    key={dest.slug}
                    to={`/destinations/${dest.slug}`}
                    className="p-3 rounded-lg bg-slate-50 hover:bg-blue-50 transition-colors text-left group"
                    data-testid={`404-destination-${dest.slug}`}
                  >
                    <div className="font-medium text-slate-800 group-hover:text-blue-600 transition-colors">
                      {dest.name}
                    </div>
                    <div className="text-sm text-slate-500">{dest.state}</div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-6 text-center text-sm text-slate-500">
          <p>Need help? <Link to="/support" className="text-blue-600 hover:underline">Contact Support</Link></p>
        </footer>
      </div>
    </>
  );
}
